// MIDAS V3 — Tag pill (history page, Phase 5)
//
// Port of `TagPill` from history-page.jsx (lines 47-75). Two variants:
//   • Platform — accent-soft chip, mono 11 uppercase "◆ PLATFORM"
//   • Machine  — card-style chip with status pip + machine name
//
// Source variables (`--accent`, `--card`, `--border`, `--ink-2`) are aliased
// in midas-v3-tokens.scss inside .midas-v3-root, so the unprefixed names work.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

export type HistoryTagPillKind = "platform" | "machine";

interface HistoryTagPillV3Props {
  kind: HistoryTagPillKind;
  /** Required when kind === "machine". Falls back to "MACHINE" if missing. */
  machineLabel?: string | null;
  /** Platform-mode rendering: localised "PLATFORM" text. Defaults to "PLATFORM". */
  platformLabel?: string;
}

const HistoryTagPillV3: React.FC<HistoryTagPillV3Props> = ({
  kind,
  machineLabel,
  platformLabel = "PLATFORM",
}) => {
  if (kind === "platform") {
    return (
      <span
        className="mono"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px 4px 8px",
          background: "var(--midas-v3-accent-soft)",
          border: "1px solid var(--midas-v3-accent-line)",
          borderRadius: 999,
          fontSize: 11,
          color: "var(--midas-v3-accent)",
          fontWeight: 600,
          letterSpacing: "0.08em",
          whiteSpace: "nowrap",
          lineHeight: 1.2,
        }}
      >
        ◆ {platformLabel}
      </span>
    );
  }

  // Machine variant — running threads default to green pip (Phase 5 spec).
  // We don't have per-thread live status here, so use a single neutral
  // green dot (matches v2's history pill default — Phase 4 locked this).
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px 4px 8px",
        background: "var(--midas-v3-card)",
        border: "1px solid var(--midas-v3-border)",
        borderRadius: 999,
        fontSize: 12,
        color: "var(--midas-v3-ink-2)",
        whiteSpace: "nowrap",
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: 1.2,
      }}
      title={machineLabel || ""}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: "var(--midas-v3-green)",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {machineLabel || "MACHINE"}
      </span>
    </span>
  );
};

export default HistoryTagPillV3;
